// Admin triage page for quality issues (QCP Phase 11).
//
// Two views in one component, switched by `selectedId`:
//   1. List — filterable, paginated table of issues.
//   2. Detail — issue fields, FSM transition controls (admin-only triggers),
//      and the full audit-event timeline.
//
// A banner is shown whenever the backend reports a non-production
// environment, because admin transitions are REAL writes against whatever
// environment this dashboard is pointed at.
//
// Backend: app/routes/quality_issues_admin.py.
// Service: frontend/src/services/qualityIssues.ts.

import { AxiosError } from 'axios';
import React, { useCallback, useEffect, useState } from 'react';

import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  qualityIssuesApi,
  SEVERITY_DISPLAY,
  STATUS_DISPLAY,
  type IssueDetailResponse,
  type QualityIssue,
  type QualityIssueCategory,
  type QualityIssueEvent,
  type QualityIssueSeverity,
  type QualityIssueSource,
  type QualityIssueStatus,
  type TransitionPayload,
  conversationHref,
  isUuid,
  orderHref,
} from '../services/qualityIssues';
import styles from './QualityIssuesPage.module.css';

const PAGE_SIZE = 50;

const STATUS_VALUES: QualityIssueStatus[] = [
  'open',
  'in_review',
  'resolved',
  'closed',
  'duplicate',
];
const SEVERITY_VALUES: QualityIssueSeverity[] = [
  'low',
  'medium',
  'high',
  'critical',
];
const CATEGORY_VALUES: QualityIssueCategory[] = [
  'packaging',
  'quality',
  'shipping',
  'billing',
  'application',
  'other',
];
const SOURCE_VALUES: QualityIssueSource[] = [
  'qcp',
  'email',
  'phone',
  'admin',
  'legacy_packaging',
];

function formatTs(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function prettify(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');
}

function statusTag(s: QualityIssueStatus): string {
  return `${styles.tag} ${styles['st_' + s] ?? ''}`;
}
function severityTag(s: QualityIssueSeverity): string {
  return `${styles.tag} ${styles['sev_' + s] ?? ''}`;
}

function errMsg(e: unknown): string {
  const ax = e as AxiosError<{ detail?: unknown }>;
  const detail = ax.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return (e as Error).message || 'request failed';
}

// --- staging / non-prod writes banner ----------------------------------

function EnvBanner({ environment }: { environment: string | null }): React.ReactElement | null {
  if (!environment || environment === 'production') return null;
  return (
    <div className={styles.banner}>
      <span className={styles.badge}>{environment}</span>
      <span>
        You are triaging the <b>{environment}</b> environment. Status changes
        here are <b>real writes</b> against the {environment} database — they do
        not affect production.
      </span>
    </div>
  );
}

// --- a single optional key/value row in the detail field grid -----------

function Field({
  k,
  v,
  mono,
}: {
  k: string;
  v: React.ReactNode;
  mono?: boolean;
}): React.ReactElement | null {
  if (v === null || v === undefined || v === '') return null;
  return (
    <>
      <div className={styles.k}>{k}</div>
      <div
        className={styles.v}
        style={mono ? { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 } : undefined}
      >
        {v}
      </div>
    </>
  );
}

// --- transition control -------------------------------------------------

function TransitionControl({
  issueId,
  allowed,
  defaultOwnerId,
  onApplied,
}: {
  issueId: string;
  allowed: QualityIssueStatus[];
  defaultOwnerId: string;
  onApplied: (next: IssueDetailResponse) => void;
}): React.ReactElement {
  const [target, setTarget] = useState<QualityIssueStatus | null>(null);
  const [note, setNote] = useState('');
  const [resolution, setResolution] = useState('');
  const [ownerId, setOwnerId] = useState(defaultOwnerId);
  const [duplicateOfId, setDuplicateOfId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = useCallback(
    (s: QualityIssueStatus) => {
      setTarget(s);
      setError(null);
      setNote('');
      setResolution('');
      setOwnerId(defaultOwnerId);
      setDuplicateOfId('');
    },
    [defaultOwnerId],
  );

  const apply = useCallback(async () => {
    if (!target) return;
    // Client-side guard so the user gets an immediate message; the server
    // validates again (source of truth).
    if (target === 'resolved' && (!resolution.trim() || !ownerId.trim())) {
      setError('Resolving requires a resolution and a resolution owner id.');
      return;
    }
    if (target === 'duplicate' && !duplicateOfId.trim()) {
      setError('Marking a duplicate requires the canonical issue id.');
      return;
    }
    const payload: TransitionPayload = { to_status: target };
    if (note.trim()) payload.note = note.trim();
    if (target === 'resolved') {
      payload.resolution = resolution.trim();
      payload.resolution_owner_id = ownerId.trim();
    }
    if (target === 'duplicate') payload.duplicate_of_id = duplicateOfId.trim();

    setSaving(true);
    setError(null);
    try {
      const next = await qualityIssuesApi.transition(issueId, payload);
      setTarget(null);
      onApplied(next);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  }, [target, note, resolution, ownerId, duplicateOfId, issueId, onApplied]);

  if (allowed.length === 0) {
    return (
      <div className={styles.txCard}>
        <h3>Change status</h3>
        <div className={styles.terminal}>No admin transitions available.</div>
      </div>
    );
  }

  return (
    <div className={styles.txCard}>
      <h3>Change status</h3>
      <div className={styles.txButtons}>
        {allowed.map((s) => (
          <button
            key={s}
            className={target === s ? styles.active : ''}
            onClick={() => pick(s)}
            disabled={saving}
          >
            {STATUS_DISPLAY[s]}
          </button>
        ))}
      </div>

      {target && (
        <div className={styles.txForm}>
          {target === 'resolved' && (
            <>
              <label>Resolution (what the team did)</label>
              <textarea
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                disabled={saving}
                placeholder="e.g. Swapped the pail and reshipped on PO 44821"
              />
              <label>Resolution owner id (user UUID)</label>
              <input
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
                disabled={saving}
                placeholder="defaults to you"
              />
            </>
          )}
          {target === 'duplicate' && (
            <>
              <label>Canonical issue id (the issue this duplicates)</label>
              <input
                value={duplicateOfId}
                onChange={(e) => setDuplicateOfId(e.target.value)}
                disabled={saving}
                placeholder="quality issue UUID"
              />
            </>
          )}
          <label>Note (optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={saving}
            placeholder="Why are you making this change?"
          />
          <div className={styles.txActions}>
            <button className={styles.apply} onClick={() => void apply()} disabled={saving}>
              {saving ? 'Applying…' : `Move to ${STATUS_DISPLAY[target]}`}
            </button>
            <button
              className={styles.cancel}
              onClick={() => setTarget(null)}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}

// --- detail view --------------------------------------------------------

function IssueDetailView({
  detail,
  currentUserId,
  canTransition,
  onApplied,
  onBack,
}: {
  detail: IssueDetailResponse;
  currentUserId: string;
  /** Admin-only: the transition POST is a real FSM write (readers never see it). */
  canTransition: boolean;
  onApplied: (next: IssueDetailResponse) => void;
  onBack: () => void;
}): React.ReactElement {
  const issue = detail.issue;
  const events: QualityIssueEvent[] = detail.events;
  return (
    <div className={styles.detail}>
      <button className={styles['back-link']} onClick={onBack}>
        ← back to list
      </button>
      <h2>
        <span className={styles.issueNo}>{issue.issue_no}</span> · {issue.title}
      </h2>
      <div className={styles.meta}>
        <span className={statusTag(issue.status)}>{STATUS_DISPLAY[issue.status]}</span>
        <span className={severityTag(issue.severity)}>
          {SEVERITY_DISPLAY[issue.severity]}
        </span>
        <span className={`${styles.tag} ${styles.st_open}`}>{prettify(issue.category)}</span>
        <span className={styles.muted}>source: {prettify(issue.source)}</span>
      </div>

      <h3>Narrative</h3>
      <div className={styles.narrative}>{issue.description}</div>

      <h3>Details</h3>
      <div className={styles.fieldGrid}>
        <Field k="Created" v={formatTs(issue.created_at)} />
        <Field k="Updated" v={formatTs(issue.updated_at)} />
        <Field k="Occurred" v={issue.occurred_at ? formatTs(issue.occurred_at) : null} />
        <Field k="Jobsite" v={issue.jobsite} />
        <Field k="CSR contact" v={issue.csr_contact} />
        <Field k="Business impact" v={issue.business_impact} />
        <Field k="Related Sage SO#" v={issue.related_sage_so_no} />
        <Field
          k="Related order"
          v={
            issue.related_order_id ? (
              <Link
                className={styles.deepLink}
                to={orderHref(issue.related_order_id)}
                title="Open the order in the portal"
              >
                {issue.related_order_id}
              </Link>
            ) : null
          }
          mono
        />
        <Field k="Acting for customer" v={issue.acting_for_customer_id} mono />
        <Field k="Reporter" v={issue.reporter_internal_user_id} mono />
        <Field
          k="Conversation"
          v={
            issue.assistant_conversation_id ? (
              <Link
                className={styles.deepLink}
                to={conversationHref(issue.assistant_conversation_id)}
                title="Open the source Merlin conversation"
              >
                {issue.assistant_conversation_id}
              </Link>
            ) : null
          }
          mono
        />
        <Field k="Tags" v={issue.tags.length ? issue.tags.join(', ') : null} />
        <Field
          k="Product SKUs"
          v={issue.product_skus.length ? `${issue.product_skus.length} referenced` : null}
        />
        <Field k="Resolution" v={issue.resolution} />
        <Field k="Resolution owner" v={issue.resolution_owner_id} mono />
        <Field k="Resolved at" v={issue.resolved_at ? formatTs(issue.resolved_at) : null} />
        <Field k="Duplicate of" v={issue.duplicate_of_id} mono />
      </div>

      {canTransition && (
        <TransitionControl
          issueId={issue.id}
          allowed={detail.allowed_transitions}
          defaultOwnerId={currentUserId}
          onApplied={onApplied}
        />
      )}

      <h3>Audit timeline ({events.length})</h3>
      <div className={styles.timeline}>
        {events.map((ev) => (
          <div key={ev.id} className={styles.event}>
            <div className={styles.line}>
              {ev.from_status ? (
                <>
                  <span className={statusTag(ev.from_status)}>
                    {STATUS_DISPLAY[ev.from_status]}
                  </span>
                  <span className={styles.arrow}>→</span>
                </>
              ) : (
                <span className={styles.muted}>created as</span>
              )}
              <span className={statusTag(ev.to_status)}>
                {STATUS_DISPLAY[ev.to_status]}
              </span>
              <span className={styles.trigger}>{ev.trigger}</span>
              <span className={styles.when}>{formatTs(ev.created_at)}</span>
            </div>
            {ev.note && <div className={styles.note}>{ev.note}</div>}
          </div>
        ))}
        {events.length === 0 && <div className={styles.empty}>(no events)</div>}
      </div>
    </div>
  );
}

// --- page ---------------------------------------------------------------

export default function QualityIssuesPage(): React.ReactElement {
  const { user } = useAuth();
  const [items, setItems] = useState<QualityIssue[]>([]);
  const [total, setTotal] = useState(0);
  const [environment, setEnvironment] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<IssueDetailResponse | null>(null);

  const [statusF, setStatusF] = useState<QualityIssueStatus | ''>('');
  const [severityF, setSeverityF] = useState<QualityIssueSeverity | ''>('');
  const [categoryF, setCategoryF] = useState<QualityIssueCategory | ''>('');
  const [sourceF, setSourceF] = useState<QualityIssueSource | ''>('');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  // Entity filters are raw text inputs (UUIDs pasted from a customer / user
  // record) — debounced like the search box so we don't hit the API per key.
  const [customerF, setCustomerF] = useState('');
  const [reporterF, setReporterF] = useState('');
  const [debouncedCustomer, setDebouncedCustomer] = useState('');
  const [debouncedReporter, setDebouncedReporter] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q);
      setDebouncedCustomer(customerF.trim());
      setDebouncedReporter(reporterF.trim());
      setOffset(0);
    }, 350);
    return () => clearTimeout(t);
  }, [q, customerF, reporterF]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Parameters<typeof qualityIssuesApi.list>[0] = {
        limit: PAGE_SIZE,
        offset,
      };
      if (statusF) params.status = statusF;
      if (severityF) params.severity = severityF;
      if (categoryF) params.category = categoryF;
      if (sourceF) params.source = sourceF;
      if (debouncedQ.trim()) params.q = debouncedQ.trim();
      if (isUuid(debouncedCustomer)) params.customer_id = debouncedCustomer;
      if (isUuid(debouncedReporter)) params.reporter_id = debouncedReporter;
      if (createdFrom) params.created_from = createdFrom;
      if (createdTo) params.created_to = createdTo;
      const data = await qualityIssuesApi.list(params);
      setItems(data.items);
      setTotal(data.total);
      setEnvironment(data.environment);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [
    statusF,
    severityF,
    categoryF,
    sourceF,
    debouncedQ,
    debouncedCustomer,
    debouncedReporter,
    createdFrom,
    createdTo,
    offset,
  ]);

  useEffect(() => {
    if (selected) return;
    void load();
  }, [load, selected]);

  const openIssue = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const detail = await qualityIssuesApi.get(id);
      setSelected(detail);
      setEnvironment(detail.environment);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  if (selected) {
    return (
      <div className={styles.page}>
        <EnvBanner environment={environment} />
        <IssueDetailView
          detail={selected}
          currentUserId={user?.id ?? ''}
          canTransition={user?.role === 'ADMIN'}
          onApplied={(next) => {
            setSelected(next);
            setEnvironment(next.environment);
          }}
          onBack={() => setSelected(null)}
        />
      </div>
    );
  }

  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className={styles.page}>
      <EnvBanner environment={environment} />

      <div className={styles.header}>
        <div>
          <h1>Quality Issues</h1>
          <div className={styles.sub}>
            Triage complaints filed through Merlin and drive their
            lifecycle.
          </div>
        </div>
      </div>

      <div className={styles.filters}>
        <label>Status</label>
        <select
          value={statusF}
          onChange={(e) => {
            setStatusF(e.target.value as QualityIssueStatus | '');
            setOffset(0);
          }}
        >
          <option value="">Any</option>
          {STATUS_VALUES.map((s) => (
            <option key={s} value={s}>
              {STATUS_DISPLAY[s]}
            </option>
          ))}
        </select>

        <label>Severity</label>
        <select
          value={severityF}
          onChange={(e) => {
            setSeverityF(e.target.value as QualityIssueSeverity | '');
            setOffset(0);
          }}
        >
          <option value="">Any</option>
          {SEVERITY_VALUES.map((s) => (
            <option key={s} value={s}>
              {SEVERITY_DISPLAY[s]}
            </option>
          ))}
        </select>

        <label>Category</label>
        <select
          value={categoryF}
          onChange={(e) => {
            setCategoryF(e.target.value as QualityIssueCategory | '');
            setOffset(0);
          }}
        >
          <option value="">Any</option>
          {CATEGORY_VALUES.map((s) => (
            <option key={s} value={s}>
              {prettify(s)}
            </option>
          ))}
        </select>

        <label>Source</label>
        <select
          value={sourceF}
          onChange={(e) => {
            setSourceF(e.target.value as QualityIssueSource | '');
            setOffset(0);
          }}
        >
          <option value="">Any</option>
          {SOURCE_VALUES.map((s) => (
            <option key={s} value={s}>
              {prettify(s)}
            </option>
          ))}
        </select>

        <input
          className={styles.search}
          placeholder="Search issue #, title, description…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className={styles.filters}>
        <label htmlFor="qi-customer">Customer</label>
        <input
          id="qi-customer"
          className={`${styles.uuidInput} ${
            customerF.trim() && !isUuid(customerF.trim()) ? styles.invalid : ''
          }`}
          placeholder="customer UUID"
          value={customerF}
          onChange={(e) => setCustomerF(e.target.value)}
          aria-invalid={Boolean(customerF.trim()) && !isUuid(customerF.trim())}
        />

        <label htmlFor="qi-reporter">Reporter</label>
        <input
          id="qi-reporter"
          className={`${styles.uuidInput} ${
            reporterF.trim() && !isUuid(reporterF.trim()) ? styles.invalid : ''
          }`}
          placeholder="reporter user UUID"
          value={reporterF}
          onChange={(e) => setReporterF(e.target.value)}
          aria-invalid={Boolean(reporterF.trim()) && !isUuid(reporterF.trim())}
        />

        <label htmlFor="qi-from">Created from</label>
        <input
          id="qi-from"
          type="date"
          value={createdFrom}
          max={createdTo || undefined}
          onChange={(e) => {
            setCreatedFrom(e.target.value);
            setOffset(0);
          }}
        />
        <label htmlFor="qi-to">to</label>
        <input
          id="qi-to"
          type="date"
          value={createdTo}
          min={createdFrom || undefined}
          onChange={(e) => {
            setCreatedTo(e.target.value);
            setOffset(0);
          }}
        />

        {(statusF || severityF || categoryF || sourceF || q || customerF || reporterF || createdFrom || createdTo) && (
          <button
            type="button"
            className={styles.clearFilters}
            onClick={() => {
              setStatusF('');
              setSeverityF('');
              setCategoryF('');
              setSourceF('');
              setQ('');
              setCustomerF('');
              setReporterF('');
              setCreatedFrom('');
              setCreatedTo('');
              setOffset(0);
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <table className={styles.list}>
        <thead>
          <tr>
            <th>Issue</th>
            <th>Title</th>
            <th>Status</th>
            <th>Severity</th>
            <th>Category</th>
            <th>Source</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr
              key={it.id}
              className={styles.clickable}
              onClick={() => void openIssue(it.id)}
            >
              <td>
                <span className={styles.issueNo}>{it.issue_no}</span>
              </td>
              <td className={styles.titleCell} title={it.title}>
                {it.title}
              </td>
              <td>
                <span className={statusTag(it.status)}>{STATUS_DISPLAY[it.status]}</span>
              </td>
              <td>
                <span className={severityTag(it.severity)}>
                  {SEVERITY_DISPLAY[it.severity]}
                </span>
              </td>
              <td>{prettify(it.category)}</td>
              <td>{prettify(it.source)}</td>
              <td className={styles.muted}>{formatTs(it.created_at)}</td>
            </tr>
          ))}
          {!loading && items.length === 0 && (
            <tr>
              <td colSpan={7} className={styles.empty}>
                no quality issues match these filters
              </td>
            </tr>
          )}
          {loading && (
            <tr>
              <td colSpan={7} className={styles.empty}>
                loading…
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className={styles.pagination}>
        <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
          ← Prev
        </button>
        <span>
          {showingFrom}–{showingTo} of {total}
        </span>
        <button
          disabled={offset + PAGE_SIZE >= total}
          onClick={() => setOffset(offset + PAGE_SIZE)}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
